import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Analytics from 'analytics-opencare';
import { Div, VisuallyHidden } from 'common/styles/oc-styled-components';
import { Signup } from 'connect/saga/handlers/user/signup';
import { selectDataLoadingUser, selectDataUser } from 'connect/selectors';
import { Form, LoadingOutlined } from 'opencare-crown';
import { useAnalyticsService } from 'services/AnalyticsService';
import { useOfferService } from 'services/OfferService';

import {
  DIMENSIONS,
  useWindowDimensions,
} from '../../../../providers/WindowDimensionsProvider';
import FloatingInput from '../../../common/components/FloatingInput/FloatingInput';
import TermsAndConditions from '../../../common/components/TermsAndConditions/TermsAndConditions';
import { checkEmail } from '../../../common/helpers/form-field-helpers';
import { Subtitle, Title } from '../../../common/styled-components';
import { useFunnelStorage } from '../../storage/Storage';
import {
  ButtonBlock,
  OptimizelyPrimaryButton,
  SecondaryButton,
  StepWrapper,
} from '../../styled-components';
import { generateStepAnalytics, STEP_KEY } from '../../utils';

import { OfferImage } from './styled-components';

const analytics = new Analytics();

const Contact = ({ position, moveToNextStep }) => {
  const dispatch = useDispatch();
  const storage = useFunnelStorage();
  const [contactForm] = Form.useForm();
  const { layout } = useWindowDimensions();

  const offerService = useOfferService();
  const analyticsService = useAnalyticsService();

  const userLoading = useSelector(selectDataLoadingUser);
  const user = useSelector(selectDataUser);

  const [email, setEmail] = useState(storage.prefillData.patientEmail || '');
  const [hasSubmitted, setSubmitted] = useState(false);
  const submitButtonRef = useRef();

  useEffect(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.FUNNEL_LEAD,
      pageName: 'funnel_contact',
      ...generateStepAnalytics(position, storage),
      ...analyticsService.analyticsData,
    });
  }, []);

  const trackSignupAndContinue = () => {
    analytics.trackSignUp({
      pageType: analytics.pageTypes.FUNNEL_LEAD,
      pageName: 'funnel_contact',
      name: 'wizard',
      section: 'contact-info',
      position: [2],
      ...generateStepAnalytics(position, storage, {
        response: `${email}`,
      }),
      userId: user.id,
      ...analyticsService.analyticsData,
    });

    storage.save(STEP_KEY.CONTACT, true, true);

    moveToNextStep();
  };

  useEffect(() => {
    if (!hasSubmitted || userLoading) {
      return;
    }

    // TODO we don't properly handle user signup errors
    // If there is an error, we should display an error message to the user
    // If the error is that the user already exists, we should log them in instead of ignoring it

    // User already exists in DB, do nothing with their info and send them on their way
    if (!user?.authenticated) {
      storage.save(STEP_KEY.CONTACT, true, true);
      moveToNextStep();
      return;
    }

    trackSignupAndContinue();
  }, [userLoading, user]);

  const skip = () => {
    storage.save(STEP_KEY.CONTACT, false, true);

    analytics.trackClick({
      pageType: analytics.pageTypes.FUNNEL_LEAD,
      pageName: 'funnel_contact',
      name: 'wizard',
      section: 'contact-info',
      position: [1],
      ...generateStepAnalytics(position, storage, {
        response: 'skip',
      }),
    });

    moveToNextStep();
  };

  const complete = () => {
    // Focus the submit button to close the mobile keyboard
    if (submitButtonRef?.current?.focus) {
      submitButtonRef.current.focus();
    }

    const userData = {
      email,
      mobilePhone: null,
      patientSignupSource: 'patient-funnel',
    };

    dispatch(Signup.createAction(userData));
    setSubmitted(true);
  };

  const showOfferImage = offerService.hasActiveOffer && offerService.offerImage;
  const { offerName, offerImage } = offerService;
  const showOffer = offerService.hasActiveOffer;
  return (
    <div>
      {showOfferImage 
        && (
          <OfferImage
            src={offerImage}
            role="presentation"
            alt=""
            data-testid="offer-image"
          />
        )
      }
      <Title data-testid="title">
        {showOffer
          ? `Activate your ${offerName}`
          : 'What is your contact info?'}
      </Title>
      <Subtitle data-testid="subtitle">
        {showOffer
          ? "Once you've visited the dentist, we'll send you instructions on how to claim."
          : 'Enable secure booking by providing your email. We respect your privacy and time.'}
      </Subtitle>
      <StepWrapper width="600px">
        <Form form={contactForm} onFinish={complete}>
          <FloatingInput
            field="email"
            type="email"
            form={contactForm}
            label="Email address"
            required
            validator={checkEmail}
            capitalize={false}
            initialValue={email}
            onChange={e => setEmail(e.target.value)}
            testId="contact-email"
          />
          <Div margin="0 0 40px 0">
            <TermsAndConditions withoutPhone />
          </Div>
          <ButtonBlock>
            <SecondaryButton
              type="button"
              data-testid="skip"
              small
              onClick={skip}
            >
              {showOffer && layout !== DIMENSIONS.MOBILE
                ? "I don't want my offer"
                : 'Skip'}
            </SecondaryButton>
            <OptimizelyPrimaryButton
              id="optimizely-contact-submit-email-only"
              data-testid="submit"
              type="submit"
              ref={submitButtonRef}
              disabled={!email}
            >
              {userLoading 
                && (
                  <>
                    <LoadingOutlined />
                    <VisuallyHidden>Loading</VisuallyHidden>
                  </>
                )
              }
              {showOffer ? 'Activate offer' : 'Continue'}
            </OptimizelyPrimaryButton>
          </ButtonBlock>
        </Form>
      </StepWrapper>
    </div>
  );
};

Contact.defaultProps = {
  position: '',
};

Contact.propTypes = {
  position: PropTypes.string,
  moveToNextStep: PropTypes.func.isRequired,
};

export default Contact;
